import { useMemo } from 'react';
import { CSSObject } from 'styled-components';
import { IButtonProps } from '~/src/components/LandingBlock/Common/Button';
import { ImagePlaceholder } from '~/src/components/LandingBlock/styled';
import { IMG_DOMAIN } from '~/src/services/blocks/templates';
import { getPartialStyles } from '~/src/helpers/get-partial-styles';
import { getTextWithTag } from '~/src/helpers/getTextWithTag';
import WebpImage from '@r1-frontend/ui-react/components/WebpImage';
import * as ST from './styled';

interface IPictureProp {
    src: string;
    webp: string;
    alt: string;
    paddingTop: string;
    paddingRight: string;
}

type IHeaderButtonProps  = Pick<
    IButtonProps,
    'buttonText' |
    'href' |
    'idForScroll' |
    'isTargetBlank' |
    'analytics' |
    'backgroundColor' |
    'backgroundHoverColor' |
    'enableCalltouch'
>
export interface IInfoBlockProps {
    blockId: string;
    isAdmin: boolean;
    subtitle?: string;
    title: string;
    text: string;
    buttonProps?: IHeaderButtonProps;
    picture?: IPictureProp;
    customStyles?: ST.IInfoBlockStyles;
    background?: string;
    additionalBlockConfig?: any;
    titleTag?: string;
    subtitleTag?: string;
}

const InfoBlock = ({
    blockId,
    isAdmin,
    subtitle,
    title,
    text,
    buttonProps,
    picture,
    customStyles,
    background,
    additionalBlockConfig,
    titleTag,
    subtitleTag,
}: IInfoBlockProps): JSX.Element => {
    const partialButtonStyles = useMemo(() => {
        return getPartialStyles(customStyles as CSSObject, 'Button');
    }, [customStyles]);

    const titleWithTag = getTextWithTag(title, titleTag);
    const subtitleWithTag = getTextWithTag(subtitle, subtitleTag);

    return (
        <ST.Wrapper id={blockId} {...customStyles} background={background} {...additionalBlockConfig}>
            <ST.Grid modeName='wide'>
                <ST.Row>
                    <ST.Col sm={7} xs={12}>
                        {subtitle && (
                            <ST.Subtitle {...customStyles} dangerouslySetInnerHTML={{ __html: subtitleWithTag }} />
                        )}
                        <ST.Title {...customStyles} dangerouslySetInnerHTML={{ __html: titleWithTag }} />
                        <ST.Text
                            {...customStyles}
                            dangerouslySetInnerHTML={{ __html: text }}
                        />
                        <ST.BtnWrap>
                            <ST.Btn
                                elemId={`${blockId}.button`}
                                href={buttonProps.href}
                                isTargetBlank={buttonProps.isTargetBlank}
                                idForScroll={buttonProps.idForScroll}
                                enableCalltouch={buttonProps.enableCalltouch}
                                analytics={buttonProps.analytics}
                                buttonText={buttonProps.buttonText}
                                backgroundColor={buttonProps.backgroundColor}
                                backgroundHoverColor={buttonProps.backgroundHoverColor}
                                customStyles={partialButtonStyles}
                            />
                        </ST.BtnWrap>
                    </ST.Col>
                    <ST.Col sm={5} xs={12}>
                        <ST.Image {...customStyles}
                            paddingTop={picture?.paddingTop}
                            paddingRight={picture?.paddingRight}
                        >
                            {picture.src
                                ? <WebpImage urlWebp={picture.webp && `${IMG_DOMAIN}${picture.webp}`}>
                                    <img src={`${IMG_DOMAIN}${picture.src}`} alt={picture.alt || title}/>
                                </WebpImage>
                                : <ImagePlaceholder width={250} height={200} isAdmin={isAdmin}/>
                            }
                        </ST.Image>
                    </ST.Col>
                </ST.Row>
            </ST.Grid>
        </ST.Wrapper>
    );
};

export default InfoBlock;
